/**
 * Hook factory for form field autocomplete
 * 
 * This hook instantiates the appropriate autocomplete hook based on the field type.
 * It uses conditional enabling to ensure only the active field's hook makes API calls.
 * 
 * Usage in Form component:
 * ```typescript
 * const autocompleteHook = field.autocompleteField 
 *   ? useFormFieldAutocomplete(field.autocompleteField)
 *   : null
 * 
 * <TextInput
 *   useAutocomplete={!!autocompleteHook}
 *   autocompleteHook={autocompleteHook}
 *   {...otherProps}
 * />
 * ```
 */

import { useArticleFieldAutocomplete, type ArticleFieldType } from '../Item/hooks/useArticleFieldAutocomplete'
import { usePropertyAutocomplete } from '../Item/hooks/usePropertyAutocomplete'
import { useCategoryAutocomplete } from '../Item/hooks/useCategoryAutocomplete'
import { useMaterialAutocomplete } from '../Item/hooks/useMaterialAutocomplete'
import { useUserAutocomplete } from '../employee/hooks/useUserAutocomplete'
import { useCountryAutocomplete } from '../Address/hooks/useCountryAutocomplete'
import { useArticlesAutocomplete } from '../Item/hooks/useArticleAutocomplete'
import { useSupplierAutocomplete } from '../Supplier/hooks/useSupplierAutocomplete'
import { useCustomerAutocomplete } from '../Customer/hooks/useCustomerAutocomplete'
import { useContactAutocomplete } from '../Contact/hooks/useContactAutocomplete'
import { useRoleAutocomplete } from '../Roles/hooks/useRoleAutocomplete'

export type AutocompleteFieldType =
  | 'barcode-types'
  | 'article-types'
  | 'families'
  | 'brands'
  | 'properties'
  | 'categories'
  | 'materials'
  | 'staff-users'
  | 'countries'
  | 'articles'
  | 'suppliers'
  | 'customers'
  | 'contacts'
  | 'roles'

/**
 * Factory hook that returns the appropriate autocomplete hook based on field type
 * 
 * @param fieldType - The type of autocomplete field
 * @returns The autocomplete hook with suggestions, isLoading, updateQuery, and query
 */
export function useFormFieldAutocomplete(fieldType: AutocompleteFieldType | string) {
  // Instantiate all hooks with conditional enabling
  // Only the matching field type will be enabled and make API calls
  
  // Article field types (use generic hook)
  const barcodeTypesHook = useArticleFieldAutocomplete('barcode-types', {
    enabled: fieldType === 'barcode-types',
    size: 10,
  })
  
  const articleTypesHook = useArticleFieldAutocomplete('article-types', {
    enabled: fieldType === 'article-types',
    size: 10,
  })
  
  const familiesHook = useArticleFieldAutocomplete('families', {
    enabled: fieldType === 'families',
    size: 10,
  })
  
  const brandsHook = useArticleFieldAutocomplete('brands', {
    enabled: fieldType === 'brands',
    size: 10,
  })
  
  // Object field types (nested in articles)
  const propertiesHook = usePropertyAutocomplete({
    enabled: fieldType === 'properties',
    size: 10,
  })
  
  const categoriesHook = useCategoryAutocomplete({
    enabled: fieldType === 'categories',
    size: 10,
  })
  
  const materialsHook = useMaterialAutocomplete({
    enabled: fieldType === 'materials',
    size: 10,
  })
  
  // User autocomplete (with staff-only filter)
  const staffUsersHook = useUserAutocomplete({
    staffOnly: true,
    enabled: fieldType === 'staff-users',
    size: 10,
  })
  
  // Country autocomplete (external API)
  const countriesHook = useCountryAutocomplete({
    enabled: fieldType === 'countries',
  })
  
  // Full object autocomplete (articles, suppliers, customers, etc.)
  const articlesHook = useArticlesAutocomplete({
    enabled: fieldType === 'articles',
    size: 10,
  })
  
  const suppliersHook = useSupplierAutocomplete({
    enabled: fieldType === 'suppliers',
    size: 10,
  })
  
  const customersHook = useCustomerAutocomplete({
    enabled: fieldType === 'customers',
    size: 10,
  })
  
  const contactsHook = useContactAutocomplete({
    enabled: fieldType === 'contacts',
    size: 10,
  })
  
  const rolesHook = useRoleAutocomplete({
    enabled: fieldType === 'roles',
    size: 10,
  })
  
  // Map field type to corresponding hook
  const hookMap: Record<string, any> = {
    'barcode-types': barcodeTypesHook,
    'article-types': articleTypesHook,
    'families': familiesHook,
    'brands': brandsHook,
    'properties': propertiesHook,
    'categories': categoriesHook,
    'materials': materialsHook,
    'staff-users': staffUsersHook,
    'countries': countriesHook,
    'articles': articlesHook,
    'suppliers': suppliersHook,
    'customers': customersHook,
    'contacts': contactsHook,
    'roles': rolesHook,
  }
  
  const selectedHook = hookMap[fieldType]
  
  if (!selectedHook || !fieldType || fieldType === '') {
    return null
  }
  
  return selectedHook
}

/**
 * Type guard to check if a field uses autocomplete
 */
export function hasAutocomplete(field: any): field is { autocompleteField: AutocompleteFieldType } {
  return field && typeof field.autocompleteField === 'string'
}

